import {
  Component,
  ElementRef,
  InputSignal,
  ModelSignal,
  OutputEmitterRef,
  Signal,
  afterRenderEffect,
  computed,
  input,
  model,
  output,
  viewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { IPageChangedEvent } from './models/page-changed-event';
import { PaginationStyle } from './models/pagination-style';

/**
 * The controller for a pagination control (numbered pages, simple, load-more, infinite scroll).
 */
@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <ng-content></ng-content>
  `
})
export class GsPaginationController {
  // Inputs & Models
  /** The current page number. (0 based index) */
  currentPage: ModelSignal<number> = model(0);
  /** The total number of pages available. */
  totalPages: InputSignal<number> = input(1);
  /** The maximum number of visible pages in the pagination control. */
  maxVisiblePages: InputSignal<number> = input(3);
  /** The style of pagination to be used. */
  paginationStyle: InputSignal<PaginationStyle> = input<PaginationStyle>(PaginationStyle.DEFAULT);
  /** The size of each page (number of items per page). */
  pageSize: InputSignal<number> = input(8);
  /** The total count of items across all pages. */
  totalCount: InputSignal<number> = input(0);
  /** Loading state for the pagination component. */
  isLoading: InputSignal<boolean> = input(false);

  // Outputs
  /** Event emitted when the page changes. */
  pageChanged: OutputEmitterRef<IPageChangedEvent> = output<IPageChangedEvent>();

  // Properties & Signals
  /** CSS class for the pagination container based on the current pagination style. */
  containerClass: Signal<string> = computed(() => {
    const pageStyle = this.paginationStyle();
    if (pageStyle === PaginationStyle.SIMPLE) return 'flex justify-center';
    if (pageStyle === PaginationStyle.LOAD_MORE) return 'flex justify-center';
    if (pageStyle === PaginationStyle.INFINITE_SCROLL) return 'flex justify-center';
    if (pageStyle === PaginationStyle.DEFAULT) return 'flex justify-between items-center';
    return '';
  });
  /** Starting record number for the current page. */
  startRecord: Signal<number> = computed(() => {
    return this.currentPage() * this.pageSize() + 1;
  });
  /** Index of the last record on the current page. */
  endRecord: Signal<number> = computed(() => {
    return (this.currentPage() + 1) * this.pageSize();
  });
  /**
   * Computes the array of visible page numbers for pagination.
   * An array of page numbers to be displayed. The array may include `-1` as a placeholder for ellipses to indicate skipped pages.
   */
  visiblePages: Signal<number[]> = computed(() => {
    const pages: number[] = [];
    const maxVisible = this.maxVisiblePages();
    const total = this.totalPages();
    const current = this.currentPage();
    const halfVisible = Math.floor(maxVisible / 2);
    let startPage = Math.max(0, current - halfVisible);
    let endPage = Math.min(total - 1, startPage + maxVisible - 1);
    if (endPage === total - 1) startPage = Math.max(0, endPage - maxVisible + 1);
    if (startPage === 0) endPage = Math.min(total - 1, maxVisible - 1);
    if (startPage > 0) {
      pages.push(0);
      if (startPage > 1) pages.push(-1);
    }
    for (let i = startPage; i <= endPage; i++) pages.push(i);
    if (endPage < total - 1) {
      if (endPage < total - 2) pages.push(-1);
      pages.push(total - 1);
    }
    return pages;
  });
  // Expose enum to subclass templates
  PaginationStyle = PaginationStyle;

  // ViewChilds
  /** Reference to the infinite scroll trigger element */
  private infiniteScrollTrigger = viewChild<ElementRef<HTMLDivElement>>('infiniteScrollTrigger');

  constructor() {
    // Re-observes the infinite scroll trigger after it got re-rendered or a load finished (browser only)
    afterRenderEffect((onCleanup) => {
      const trigger = this.infiniteScrollTrigger()?.nativeElement;
      if (this.paginationStyle() !== PaginationStyle.INFINITE_SCROLL || !trigger || this.isLoading()) return;
      const observer = this.createIntersectionObserver();
      observer.observe(trigger);
      onCleanup(() => observer.disconnect());
    });
  }

  // Methods
  /**
   * Handles the page change event.
   * @param page - The new page number. If not provided, it defaults to the next page.
   */
  onPageChange(page?: number): void {
    page = page ?? this.currentPage() + 1;
    const pagination = this.paginationStyle();
    if (page >= 0 && page < this.totalPages() && page !== this.currentPage()) {
      this.pageChanged.emit({
        pageNumber: page,
        paginationStyle: pagination
      });
      this.currentPage.set(page);
    }
  }

  /**
   * Creates the Intersection Observer that loads the next page once the trigger becomes visible.
   * @returns the observer, not yet observing any element
   */
  private createIntersectionObserver(): IntersectionObserver {
    return new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        // Load next page when trigger becomes visible and not already loading
        if (entry.isIntersecting && !this.isLoading() && this.currentPage() < this.totalPages() - 1) {
          this.onPageChange();
        }
      },
      {
        // Trigger when element is 100px away from viewport
        rootMargin: '100px',
        threshold: 0
      }
    );
  }
}
